import axios from 'axios'
import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import type { ImportQualificationValidationsParams } from '@/Network/Types/Requests/QualificationPolicyRequests'
import type {
  ImportValidationsResponse,
  ValidateImportSchedulesResponse
} from '@/Network/Types/Responses/QualificationPolicyResponses'

type ImportPolicyPrefix = 'qualification-transfer-policy' | 'qualification-documental-policy'

// Domínio nomeado nas mensagens de erro: as duas instâncias compartilham os
// métodos, e sem isso o usuário não saberia qual importação falhou.
const POLICY_DOMAIN_LABEL: Record<ImportPolicyPrefix, string> = {
  'qualification-transfer-policy': 'qualificação para repasse',
  'qualification-documental-policy': 'qualificação documental'
}

class QualificationPolicyImportMethods extends Service {
  policyType: ImportPolicyPrefix
  private readonly domainLabel: string

  constructor(policyType: ImportPolicyPrefix) {
    super()
    this.prefix = policyType
    this.policyType = policyType
    this.domainLabel = POLICY_DOMAIN_LABEL[policyType]
  }

  async importValidations(
    params: ImportQualificationValidationsParams,
    onUploadProgress?: (event: ProgressEvent) => void,
    signal?: AbortSignal
  ): Promise<ImportValidationsResponse> {
    try {
      const formData = new FormData()
      formData.append('file', params.file)
      params.scheduleIds.forEach((id) => formData.append('scheduleIds', String(id)))

      const response = await request.post<ImportValidationsResponse>(
        this.getCompletePath(`/import?companyId=${params.companyId}`),
        formData,
        { onUploadProgress, signal }
      )
      return response.data
    } catch (e) {
      if (axios.isCancel(e)) throw e
      throw this.handleError(e, `Não foi possível importar as validações de ${this.domainLabel}.`)
    }
  }

  async validateImportSchedules(
    companyId: number,
    scheduleIds: number[]
  ): Promise<ValidateImportSchedulesResponse> {
    try {
      const response = await request.post<ValidateImportSchedulesResponse>(
        this.getCompletePath('/validate-import-schedules'),
        { companyId, scheduleIds }
      )
      return response.data
    } catch (e) {
      throw this.handleError(
        e,
        `Não foi possível verificar as escalas para a importação de ${this.domainLabel}.`
      )
    }
  }

  async downloadImportTemplate(policyType: string): Promise<Blob> {
    try {
      const response = await request.get<Blob>(
        this.getCompletePath(`/import-template/${policyType}`),
        undefined,
        undefined,
        'blob'
      )
      return response.data
    } catch (e) {
      // responseType 'blob': o body JSON do erro chega dentro de um Blob.
      throw this.handleError(
        await unwrapBlobError(e),
        `Não foi possível baixar o modelo de importação de ${this.domainLabel}.`
      )
    }
  }
}

export default class QualificationImportService {
  qualificationTransferPolicy: QualificationPolicyImportMethods
  qualificationDocumentalPolicy: QualificationPolicyImportMethods

  constructor() {
    this.qualificationTransferPolicy = new QualificationPolicyImportMethods(
      'qualification-transfer-policy'
    )
    this.qualificationDocumentalPolicy = new QualificationPolicyImportMethods(
      'qualification-documental-policy'
    )
  }
}
